import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Building2, Mail, Phone, MapPin, FileText, CheckCircle, Clock, Lock, Loader2, Eye, EyeOff, Upload, Trash2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

// Company Logo Upload Component
const CompanyLogoUpload = ({ user, onUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, or WEBP');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/customer/upload-logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Logo uploaded successfully!');
      // Trigger parent update to refresh user data
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete your company logo?')) return;

    setDeleting(true);
    try {
      await axios.delete(`${API}/customer/delete-logo`);
      toast.success('Logo deleted successfully!');
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error deleting logo:', error);
      toast.error('Failed to delete logo');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Current Logo Display */}
      <div className="flex items-center gap-6">
        {user?.company_logo ? (
          <div className="relative">
            <img
              src={user.company_logo}
              alt={user.company_name}
              className="h-24 w-24 object-contain rounded border-2 border-gray-300"
            />
          </div>
        ) : (
          <div className="h-24 w-24 rounded bg-blue-600 flex items-center justify-center text-white font-bold text-3xl border-2 border-blue-700">
            {user?.company_name?.substring(0, 2).toUpperCase() || "CO"}
          </div>
        )}
        
        <div className="flex-1">
          <p className="text-sm text-gray-600 mb-2">
            {user?.company_logo ? 'Current company logo' : 'No logo uploaded yet'}
          </p>
          <p className="text-xs text-gray-500">
            Recommended: Square image, max 5MB (JPEG, PNG, or WEBP)
          </p>
        </div>
      </div>

      {/* Upload/Delete Buttons */}
      <div className="flex gap-2">
        <label htmlFor="logo-upload">
          <Button
            type="button"
            disabled={uploading}
            className="!bg-blue-600 hover:!bg-blue-700 !text-white cursor-pointer"
            onClick={() => document.getElementById('logo-upload').click()}
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" />{user?.company_logo ? 'Change Logo' : 'Upload Logo'}</>
            )}
          </Button>
        </label>
        <input
          id="logo-upload"
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        {user?.company_logo && (
          <Button
            onClick={handleDelete}
            disabled={deleting}
            className="!bg-red-600 hover:!bg-red-700 !text-white"
          >
            {deleting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>
            ) : (
              <><Trash2 className="w-4 h-4 mr-2" />Delete Logo</>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

const CustomerSettingsTab = ({ user, onUpdate }) => {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const getStatusBadge = () => {
    if (user.status === 'approved') {
      return <Badge className="bg-green-600 text-white"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
    } else if (user.status === 'pending') {
      return <Badge className="bg-yellow-600 text-white"><Clock className="w-3 h-3 mr-1" />Pending Approval</Badge>;
    } else {
      return <Badge variant="outline">{user.status}</Badge>;
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      
      toast.success("Password changed successfully!");
      setShowPasswordForm(false);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Account Settings</h2>
        <p className="text-gray-600">View your account information</p>
      </div>

      {/* Account Status */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader>
          <CardTitle>Account Status</CardTitle>
          <CardDescription>Current approval status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            {getStatusBadge()}
            {user.status === 'pending' && (
              <p className="text-sm text-gray-600">Your account is awaiting admin approval</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Company Logo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Company Logo
          </CardTitle>
          <CardDescription>Upload your company logo to personalize your dashboard</CardDescription>
        </CardHeader>
        <CardContent>
          <CompanyLogoUpload user={user} onUpdate={onUpdate} />
        </CardContent>
      </Card>

      {/* Company Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Company Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Company Name</p>
              <p className="font-semibold text-gray-800">{user.company_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">VAT Number</p>
              <p className="font-semibold text-gray-800">{user.vat_number || 'N/A'}</p>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600 flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              Address
            </p>
            <p className="font-semibold text-gray-800">{user.address || 'N/A'}</p>
          </div>
        </CardContent>
      </Card>

      {/* Contact Person */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Contact Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">First Name</p>
              <p className="font-semibold text-gray-800">{user.first_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Last Name</p>
              <p className="font-semibold text-gray-800">{user.last_name}</p>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600">Email</p>
            <p className="font-semibold text-gray-800">{user.email}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 flex items-center gap-1">
              <Phone className="w-4 h-4" />
              Phone
            </p>
            <p className="font-semibold text-gray-800">{user.phone || 'N/A'}</p>
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle>Account Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div>
            <p className="text-sm text-gray-600">Role</p>
            <Badge variant="outline">{user.role}</Badge>
          </div>
          <div>
            <p className="text-sm text-gray-600">Account Created</p>
            <p className="text-gray-800">{new Date(user.created_at).toLocaleDateString()}</p>
          </div>
        </CardContent>
      </Card>

      {/* Password Change */}
      <Card className="border-l-4 border-l-gold-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent>
          {!showPasswordForm ? (
            <Button
              onClick={() => setShowPasswordForm(true)}
              className="!bg-gold-600 hover:!bg-gold-700 !text-white"
            >
              Change Password
            </Button>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    required
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    required
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Must be 8+ characters with uppercase, lowercase, number, and special character
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
                  }}
                  className="flex-1 !bg-gray-300 hover:!bg-gray-400 !text-gray-800"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={changingPassword}
                  className="flex-1 !bg-gold-600 hover:!bg-gold-700 !text-white"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    "Update Password"
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Note */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> To update your company or contact information, please contact our support team at support@playbacktoken.com
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerSettingsTab;
